import { useState } from 'react';
import type { LeadStatus, PropertyType } from '../types';
import { PROPERTY_TYPE_LABELS, STATUS_LABELS } from '../types';

interface FilterBarProps {
  onFilter: (filters: FilterState) => void;
  initialValues?: FilterState;
}

export interface FilterState {
  status: LeadStatus | '';
  prop_type: PropertyType | '';
  search: string;
}

export function FilterBar({ onFilter, initialValues }: FilterBarProps) {
  const [filters, setFilters] = useState<FilterState>(
    initialValues || { status: '', prop_type: '', search: '' }
  );

  const handleChange = (key: keyof FilterState, value: string) => {
    const newFilters = { ...filters, [key]: value } as FilterState;
    setFilters(newFilters);
    onFilter(newFilters);
  };

  const handleReset = () => {
    const resetFilters: FilterState = { status: '', prop_type: '', search: '' };
    setFilters(resetFilters);
    onFilter(resetFilters);
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex flex-wrap gap-4">
        {/* Status filter */}
        <div className="flex-1 min-w-[150px]">
          <label htmlFor="status" className="block text-xs font-medium text-gray-500 mb-1">
            Status
          </label>
          <select
            id="status"
            value={filters.status}
            onChange={(e) => handleChange('status', e.target.value as LeadStatus | '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Property type filter */}
        <div className="flex-1 min-w-[150px]">
          <label htmlFor="prop_type" className="block text-xs font-medium text-gray-500 mb-1">
            Property Type
          </label>
          <select
            id="prop_type"
            value={filters.prop_type}
            onChange={(e) => handleChange('prop_type', e.target.value as PropertyType | '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Types</option>
            {Object.entries(PROPERTY_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {/* Search filter */}
        <div className="flex-[2] min-w-[200px]">
          <label htmlFor="search" className="block text-xs font-medium text-gray-500 mb-1">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              id="search"
              value={filters.search}
              onChange={(e) => handleChange('search', e.target.value)}
              placeholder="Search by name, address, phone..."
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Reset button */}
        <div className="flex items-end">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
